import type { LojaAtualResponse } from '../api';
import { getTabletConfig } from './tablet-config';

/**
 * Contexto de "atendimento no salão" (tablet fixo OU mesa/comanda por QR).
 *
 * Regra central: a CONTA (pedido + total consumido) é sempre chaveada pelo
 * **identificador principal** configurado (`tipoIdentificadorPrincipal`), não pelo
 * tipo do QR. Se o QR não é do tipo principal (ex.: QR de MESA numa loja cujo
 * principal é COMANDA), o principal precisa ser PERGUNTADO ao cliente, e o QR
 * passa a preencher o secundário (a localização física).
 */
export interface ContextoSalao {
  /** É atendimento de salão (tablet/mesa/comanda)? */
  ativo: boolean;
  /** Tipo da chave da conta (do config; fallback = tipo do QR). */
  tipoPrincipal: 'MESA' | 'COMANDA';
  /** 'mesa' | 'comanda' — para rótulos de campo/prompt. */
  rotuloPrincipal: 'mesa' | 'comanda';
  /** Sabemos o número do principal sem perguntar (veio do QR/aparelho)? */
  principalConhecido: boolean;
  /** Número do principal, quando conhecido. */
  principalNumero: string;
  /** Número do secundário preenchido pelo QR/aparelho (localização física), quando houver. */
  secundarioNumero: string;
  /** Nº da mesa física (para "chamar garçom"); null quando não há localização física. */
  mesaFisica: string | null;
  /** Mesa/comanda por QR SEM `tipoIdentificadorPrincipal` configurado (bloqueia o modo). */
  principalNaoConfigurado: boolean;
}

const INATIVO: ContextoSalao = {
  ativo: false,
  tipoPrincipal: 'MESA',
  rotuloPrincipal: 'mesa',
  principalConhecido: false,
  principalNumero: '',
  secundarioNumero: '',
  mesaFisica: null,
  principalNaoConfigurado: false,
};

export function contextoSalao(loja: LojaAtualResponse | undefined): ContextoSalao {
  if (!loja) return INATIVO;
  const ident = loja.identificador;
  const cfg = loja.configuracao;

  // TABLET: dispositivo fixo na mesa. A mesa vem do aparelho; no modo COMANDA a
  // comanda (principal) é perguntada. Sempre há mesa física (chamar garçom ok).
  if (ident.tipo === 'TABLET') {
    const modo: 'MESA' | 'COMANDA' = cfg.tabletIdentificador === 'COMANDA' ? 'COMANDA' : 'MESA';
    const mesa = getTabletConfig().mesa.trim();
    return {
      ativo: true,
      tipoPrincipal: modo,
      rotuloPrincipal: modo === 'COMANDA' ? 'comanda' : 'mesa',
      principalConhecido: modo === 'MESA',
      principalNumero: modo === 'MESA' ? mesa : '',
      secundarioNumero: modo === 'COMANDA' ? mesa : '',
      mesaFisica: mesa || null,
      principalNaoConfigurado: false,
    };
  }

  // MESA/COMANDA por QR.
  if (ident.tipo === 'MESA' || ident.tipo === 'COMANDA') {
    const p = cfg.tipoIdentificadorPrincipal;
    const tipoPrincipal: 'MESA' | 'COMANDA' = p === 'MESA' || p === 'COMANDA' ? p : ident.tipo;
    const qrEhPrincipal = ident.tipo === tipoPrincipal;
    const numero = ident.numero ?? '';
    return {
      ativo: true,
      tipoPrincipal,
      rotuloPrincipal: tipoPrincipal === 'COMANDA' ? 'comanda' : 'mesa',
      principalConhecido: qrEhPrincipal,
      principalNumero: qrEhPrincipal ? numero : '',
      // QR de tipo diferente do principal → ele é o secundário (a localização física).
      secundarioNumero: qrEhPrincipal ? '' : numero,
      // A mesa física só é conhecida quando o QR é de MESA.
      mesaFisica: ident.tipo === 'MESA' ? numero || null : null,
      principalNaoConfigurado: !p,
    };
  }

  return INATIVO;
}
